import React, { FunctionComponent, useState } from "react";
import ChevronIcon from "@svg-icons/chevron.component.svg";
import { useSortBy } from "react-instantsearch-hooks-web";
import type { SortByItem } from "instantsearch.js/es/connectors/sort-by/connectSortBy";
import { Icon } from "@shared-components/Icon/Icon";
import { Box } from "@shared-components/Box/Box";
import { Button } from "theme-ui";
import { useSiteContext } from "../../../context/site/useSiteContext";
import { useDictionaryContext } from "../../../context/dictionary/useDictionaryContext";
import { DICTIONARY_MAP } from "../../../carlsberg-headless/dictionary-map";

type SortingProps = Record<string, unknown>;

export const Sorting: FunctionComponent<SortingProps> = () => {
  const { get } = useDictionaryContext();
  const { currentCulture } = useSiteContext();
  const sortingOptions: SortByItem[] = [
    {
      label:
        get(
          DICTIONARY_MAP.kiosk.kiosk_filters_sortby_brand_az,
          currentCulture
        ) || "",
      value:
        currentCulture === "en-us"
          ? "bottle_collection_sort_brand_a-z"
          : "bottle_collection_dk_sort_brand_a-z",
    },
    {
      label:
        get(
          DICTIONARY_MAP.kiosk.kiosk_filters_sortby_brand_za,
          currentCulture
        ) || "",
      value:
        currentCulture === "en-us"
          ? "bottle_collection_sort_brand_z-a"
          : "bottle_collection_dk_sort_brand_z-a",
    },
    {
      label:
        get(
          DICTIONARY_MAP.kiosk.kiosk_filters_sortby_beer_az,
          currentCulture
        ) || "",
      value:
        currentCulture === "en-us"
          ? "bottle_collection_sort_beer_a-z"
          : "bottle_collection_dk_sort_beer_a-z",
    },
    {
      label:
        get(
          DICTIONARY_MAP.kiosk.kiosk_filters_sortby_beer_za,
          currentCulture
        ) || "",
      value:
        currentCulture === "en-us"
          ? "bottle_collection_sort_beer_z-a"
          : "bottle_collection_dk_sort_beer_z-a",
    },
    {
      label:
        get(
          DICTIONARY_MAP.kiosk.kiosk_filters_sortby_alcohol_high,
          currentCulture
        ) || "",
      value:
        currentCulture === "en-us"
          ? "bottle_collection_sort_most_alcohol"
          : "bottle_collection_dk_sort_most_alcohol",
    },
    {
      label:
        get(
          DICTIONARY_MAP.kiosk.kiosk_filters_sortby_alcohol_low,
          currentCulture
        ) || "",
      value:
        currentCulture === "en-us"
          ? "bottle_collection_sort_least_alcohol"
          : "bottle_collection_dk_sort_least_alcohol",
    },
    {
      label:
        get(DICTIONARY_MAP.kiosk.kiosk_filters_sortby_newest, currentCulture) ||
        "",
      value:
        currentCulture === "en-us"
          ? "bottle_collection_sort_newest"
          : "bottle_collection_dk_sort_newest",
    },
    {
      label:
        get(DICTIONARY_MAP.kiosk.kiosk_filters_sortby_oldest, currentCulture) ||
        "",
      value:
        currentCulture === "en-us"
          ? "bottle_collection_sort_oldest"
          : "bottle_collection_dk_sort_oldest",
    },
  ];

  const [showSorting, setShowSorting] = useState(false);

  const handleSorting = (sortingValue: string) => {
    setShowSorting(false);
    refine(sortingValue);
  };

  const { currentRefinement, options, refine } = useSortBy({
    items: sortingOptions,
  });

  const currentSorting = sortingOptions.find(
    (sorting) => sorting.value === currentRefinement
  );

  return (
    <Box
      sx={{
        pos: "relative",
        textAlign: "start",
        fontWeight: "bold",
        color: "darkGreen",
        display: "flex",
        mt: [2, 0],
        alignItems: "center",
        columnGap: 2,
      }}
    >
      <Box
        sx={{
          background: "transparent",
          color: "darkGreen",
          px: 0,
          fontFamily: "NyCarlsbergDisplay-Bold",
          fontSize: ["smText", "paragraphMedium"],
          mr: 1,
          whiteSpace: "nowrap",
        }}
      >
        {get(DICTIONARY_MAP.kiosk.kiosk_filters_sortby, currentCulture)}
      </Box>
      <Box
        sx={{ bg: "lighterGray", position: "relative", minWidth: "191px" }}
        className="customSelect"
      >
        <Button
          sx={{
            display: "flex",
            justifyContent: "space-between",
            width: "100%",
            background: "transparent",
            color: "darkGreen",
            fontFamily: "NyCarlsbergDisplay-Bold",
            fontSize: ["smText", "paragraphMedium"],
            px: [2, 3],
            borderRadius: 4,
            cursor: "pointer",
            "&:focus": {
              boxShadow: "none",
            },
          }}
          onClick={() => setShowSorting(!showSorting)}
        >
          {currentSorting?.label}{" "}
          <Icon
            svg={ChevronIcon}
            sx={{
              transform: `rotateX(${showSorting ? "180deg" : "0deg"})`,
              stroke: "darkGreen",
              ml: [1, 3],
              alignSelf: "center",
            }}
          />
        </Button>
        <Box
          className="customSelectListContainer"
          sx={{
            display: showSorting ? "block" : "none",
            position: "absolute",
            width: "100%",
            zIndex: 10,
            bg: "lighterGray",
            textAlign: "center",
            px: [2, 3],
            pt: 4,
            pb: 0,
          }}
        >
          <Box
            as={"ul"}
            sx={{
              whiteSpace: "nowrap",
              textAlign: "start",
              fontSize: ["smText", "paragraph"],
              margin: 0,
              padding: 0,
            }}
          >
            {options.map(({ label, value }, key) => (
              <Box
                as={"li"}
                key={key}
                sx={{
                  listStyle: "none",
                  borderTop: "1px solid",
                  borderBottomColor: "text",
                }}
              >
                <Button
                  sx={{
                    py: [3, 4],
                    px: 0,
                    background: "transparent",
                    color: "text",
                    fontSize: ["smText", "paragraphSmall"],
                    borderBottom: "1px solid",
                    borderBottomColor: "text",
                    cursor: "pointer",
                    textAlign: "left",
                    transition: "background-color 200ms",
                    width: "full",
                    ":hover": {
                      backgroundColor: "lightGray",
                    },
                    "&:focus": {
                      boxShadow: "none",
                    },
                    ":last-child": { borderBottom: "none" },
                  }}
                  onClick={() => handleSorting(value)}
                >
                  {label}
                </Button>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
